import { useEffect, useState } from 'react';
import { CheckCircle2, ShieldAlert } from 'lucide-react';
import { Button } from '@kop/ui';
import { HazardOwnReports } from './hazard-own-reports';

import {
  buildHazardReportSubmission,
  hazardImmediateActions,
  hazardReportBlockingReason,
  hazardReportDescriptionLimit,
  hazardReportLocationLimit,
  hazardTypes,
  initialHazardReportDraft,
  type HazardReportDraft,
} from './model';
import {
  createHazardReportIntake,
  type HazardReportIntake,
  type HazardReportScope,
} from './repository';

/**
 * 현장 위험요인 제안. Two required fields and one action, so a person who has just seen a hazard
 * can choose, describe and submit without leaving the screen they are on.
 */
export function HazardReportForm({
  identity,
  scope,
}: {
  identity: { id: string; name: string };
  scope: HazardReportScope;
}) {
  const [draft, setDraft] = useState<HazardReportDraft>({ ...initialHazardReportDraft });
  const [intake, setIntake] = useState<HazardReportIntake | null>(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState<'write' | 'mine'>('write');

  useEffect(() => {
    let active = true;
    void createHazardReportIntake().then((resolved) => {
      if (active) setIntake(resolved);
    }).catch(() => {
      if (active) setIntake({status:'unavailable',reason:'접수 창구에 연결하지 못했습니다. 다시 열어 주세요.'});
    });
    return () => {
      active = false;
    };
  }, []);

  const blocking = hazardReportBlockingReason(draft);
  const submit = async () => {
    if (intake?.status !== 'ready') return;
    setBusy(true);
    setMessage('');
    try {
      const now = new Date().toISOString();
      const submission = buildHazardReportSubmission(draft, { now });
      const persisted = await intake.repository.submit({
        scope,
        hazardReportId: submission.hazardReportId,
        commandId: `submit-${submission.hazardReportId}`,
        idempotencyKey: `submit-${identity.id}-${submission.hazardReportId}`,
        report: submission.report,
      });
      setDraft({ ...initialHazardReportDraft });
      setView('mine');
      setMessage(
        `현장 위험요인이 접수되었습니다. 접수 번호 ${persisted.hazardReportId} · 안전관리자가 확인합니다.`,
      );
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '접수하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const tabs = <nav className="feedback-tabs" aria-label="위험요인 제안 메뉴">
    <button type="button" className={view==='write'?'active':''} disabled={busy} onClick={()=>setView('write')}>제안 작성</button>
    <button type="button" className={view==='mine'?'active':''} disabled={busy} onClick={()=>setView('mine')}>내 제안</button>
  </nav>;
  if(view==='mine') return <>{tabs}{message&&<p role="status">{message}</p>}<HazardOwnReports intake={intake} scope={scope}/></>;
  return (
    <>
      {tabs}
      <div className="feedback-dialog-categories" role="group" aria-label="위험 유형">
        {hazardTypes.map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={draft.hazardType === value ? 'selected' : ''}
            aria-pressed={draft.hazardType === value}
            onClick={() => setDraft((current) => ({ ...current, hazardType: value }))}
          >
            {draft.hazardType === value && <CheckCircle2 size={14} />}
            {label}
          </button>
        ))}
      </div>

      <label className="feedback-dialog-description-field">
        발견 위치
        <input
          type="text"
          value={draft.location}
          maxLength={hazardReportLocationLimit}
          placeholder="예: 2공장 압출 3호기 뒤편 통로"
          onChange={(event) =>
            setDraft((current) => ({ ...current, location: event.target.value }))
          }
        />
      </label>

      <label className="feedback-dialog-description-field">
        위험 내용
        <textarea
          value={draft.description}
          maxLength={hazardReportDescriptionLimit}
          placeholder="무엇이 위험하고, 그대로 두면 누가 어떻게 다칠 수 있는지 적어 주세요."
          onChange={(event) =>
            setDraft((current) => ({ ...current, description: event.target.value }))
          }
        />
      </label>

      <div className="feedback-dialog-categories" role="group" aria-label="즉시 조치">
        {hazardImmediateActions.map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={draft.immediateAction === value ? 'selected' : ''}
            aria-pressed={draft.immediateAction === value}
            onClick={() => setDraft((current) => ({ ...current, immediateAction: value }))}
          >
            {draft.immediateAction === value && <CheckCircle2 size={14} />}
            {label}
          </button>
        ))}
      </div>

      {intake?.status === 'unavailable' && (
        <p className="feedback-dialog-hold" role="status">
          <ShieldAlert size={14} />
          {intake.reason}
        </p>
      )}
      {message && (
        <p className="feedback-dialog-message" role="status">
          {message}
        </p>
      )}

      <footer>
        <Button
          disabled={busy || intake?.status !== 'ready' || blocking !== null}
          onClick={() => void submit()}
        >
          <ShieldAlert size={16} /> 접수
        </Button>
      </footer>
      <small className="feedback-dialog-trust">
        <ShieldAlert size={14} />
        {blocking ?? '작성한 위험요인은 안전관리자에게 전달되며, 서버 확인 후에만 접수됩니다.'}
      </small>
    </>
  );
}
